'use client'

import { ArrowUp, Clock, TrendingUp, User } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

interface Contact {
  id: number
  name: string
  email: string
  avatar: string
  segment?: string
  segmentColor?: string
}

const contacts: Array<Contact> = [
  {
    id: 1,
    name: 'Sarah Chen',
    email: 'sarah.chen@company.com',
    avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
  },
  {
    id: 2,
    name: 'Alex Rivera',
    email: 'alex.r@techcorp.com',
    avatar: 'https://randomuser.me/api/portraits/men/2.jpg',
  },
  {
    id: 3,
    name: 'Emma Wilson',
    email: 'emma@startup.io',
    avatar: 'https://randomuser.me/api/portraits/women/3.jpg',
  },
  {
    id: 4,
    name: 'James Park',
    email: 'james.park@design.co',
    avatar: 'https://randomuser.me/api/portraits/men/4.jpg',
  },
  {
    id: 5,
    name: 'Maya Patel',
    email: 'maya@agency.com',
    avatar: 'https://randomuser.me/api/portraits/women/5.jpg',
  },
  {
    id: 6,
    name: 'David Kim',
    email: 'david.kim@enterprise.com',
    avatar: 'https://randomuser.me/api/portraits/men/6.jpg',
  },
]

const CONTACT_ADDED_DATES = [
  'Jan 15, 2024',
  'Feb 3, 2024',
  'Mar 12, 2024',
  'Apr 8, 2024',
  'May 22, 2024',
  'Jun 5, 2024',
] as const

const CONTACT_LAST_OPENED = [
  '2 days ago',
  '1 week ago',
  '3 weeks ago',
  '45 days ago',
  '60 days ago',
  '90 days ago',
] as const

interface SegmentationBentoAnimationProps {
  isHovered?: boolean
}

export function SegmentationBentoAnimation({
  isHovered = false,
}: SegmentationBentoAnimationProps) {
  const ref = useRef(null)
  const [searchText, setSearchText] = useState('')
  const [isTableVisible, setIsTableVisible] = useState(false)
  const [segmentedContacts, setSegmentedContacts] =
    useState<Array<Contact>>(contacts)
  const [areSegmentsVisible, setAreSegmentsVisible] = useState(false)
  const [isTyping, setIsTyping] = useState(false)

  const searchQuery = 'Contacts who opened an email in the last 30 days'

  useEffect(() => {
    if (!isHovered) {
      // Reset all states when not hovered
      setSearchText('')
      setIsTableVisible(false)
      setSegmentedContacts(contacts)
      setAreSegmentsVisible(false)
      setIsTyping(false)
      return
    }

    // Start typing animation
    const typingTimeout = setTimeout(() => {
      setIsTyping(true)
      let currentIndex = 0
      const typingInterval = setInterval(() => {
        if (currentIndex <= searchQuery.length) {
          setSearchText(searchQuery.slice(0, currentIndex))
          currentIndex++
        } else {
          clearInterval(typingInterval)
          setIsTyping(false)
          // Show table immediately after typing completes
          setIsTableVisible(true)
          setSegmentedContacts(
            contacts.map((contact, index) => {
              let segment = ''
              let segmentColor = ''

              if (index < 2) {
                segment = 'VIP'
                segmentColor = '#8B5CF6'
              } else if (index < 4) {
                segment = 'Active'
                segmentColor = '#3B82F6'
              } else {
                segment = 'New'
                segmentColor = '#10B981'
              }

              return { ...contact, segment, segmentColor }
            })
          )
          setAreSegmentsVisible(true)
        }
      }, 30)

      return () => clearInterval(typingInterval)
    }, 300)

    return () => {
      clearTimeout(typingTimeout)
    }
  }, [isHovered])

  return (
    <div
      ref={ref}
      className="relative h-full w-full overflow-hidden [mask-image:linear-gradient(to_top,transparent_5%,#000_100%)]"
    >
      {/* Search Bar */}
      <motion.div
        className="absolute top-6 right-0 left-0 z-10 px-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          opacity: { duration: 0.5 },
          y: { duration: 0.5 },
        }}
      >
        <div className="relative mx-auto max-w-4xl">
          <div className="relative flex items-center gap-2 rounded-full border border-muted-foreground/20 bg-white p-1 dark:bg-white">
            <input
              type="text"
              value={searchText}
              readOnly
              placeholder=""
              className="w-full border-none bg-transparent py-2 pr-4 pl-4 text-sm text-zinc-900 outline-none focus:ring-0"
            />
            {!searchText && !isTyping && (
              <div className="pointer-events-none absolute top-1/2 left-4 flex -translate-y-1/2 select-none items-center gap-1">
                <p className="font-medium text-muted-foreground text-sm">
                  Ask Brew to filter for
                </p>
                <p className="font-medium text-muted-foreground text-sm">
                  engaged subscribers...
                </p>
              </div>
            )}
            <div className="shrink-0 rounded-full bg-zinc-900 p-2 text-white dark:bg-zinc-900">
              <ArrowUp className="h-4 w-4" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Table Container */}
      <AnimatePresence>
        {isTableVisible ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              ease: [0.4, 0, 0.2, 1],
            }}
            className="absolute inset-x-0 top-20 bottom-4 px-4"
          >
            <div className="mx-auto h-full max-w-4xl overflow-hidden rounded-xl border border-border bg-white shadow-sm dark:bg-zinc-900">
              <div className="h-full overflow-hidden">
                <table className="w-full">
                  <thead className="sticky top-0 border-border border-b bg-zinc-50 dark:bg-zinc-800">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-xs text-zinc-600 uppercase tracking-wide dark:text-zinc-400">
                        Email
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold text-xs text-zinc-600 uppercase tracking-wide dark:text-zinc-400">
                        Status
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold text-xs text-zinc-600 uppercase tracking-wide dark:text-zinc-400">
                        Date Added
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold text-xs text-zinc-600 uppercase tracking-wide dark:text-zinc-400">
                        Last Opened
                      </th>
                      <th className="px-4 py-2.5 text-right font-semibold text-xs text-zinc-600 uppercase tracking-wide dark:text-zinc-400">
                        Segment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {segmentedContacts.map((contact, index) => (
                      <motion.tr
                        key={contact.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: index * 0.08,
                          duration: 0.3,
                        }}
                        className="border-border border-b transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-mono text-sm text-zinc-900 dark:text-zinc-100">
                            {contact.email}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 font-medium text-green-700 text-xs dark:bg-green-900/20 dark:text-green-400">
                            Subscribed
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs text-zinc-600 dark:text-zinc-400">
                            {CONTACT_ADDED_DATES[index] ??
                              CONTACT_ADDED_DATES.at(-1)}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs text-zinc-600 dark:text-zinc-400">
                            {CONTACT_LAST_OPENED[index] ??
                              CONTACT_LAST_OPENED.at(-1)}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {contact.segment && areSegmentsVisible ? (
                            <div
                              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium text-xs"
                              style={{
                                backgroundColor: `${contact.segmentColor}15`,
                                color: contact.segmentColor,
                              }}
                            >
                              {contact.segment === 'VIP' && (
                                <TrendingUp className="h-3.5 w-3.5" />
                              )}
                              {contact.segment === 'Active' && (
                                <Clock className="h-3.5 w-3.5" />
                              )}
                              {contact.segment === 'New' && (
                                <User className="h-3.5 w-3.5" />
                              )}
                              <span>{contact.segment}</span>
                            </div>
                          ) : null}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
